/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DictionariesCollectionService, DictionaryEntry, } from '../data/dictionaries/dictionaries-collection.service';
import { UserApiService } from './api/user-api.service';
import {
  NotificationsPush,
  UserInterests,
  UserNotifications,
  UserOnboardingLevel,
  UserProfile,
  UserProfileResource,
  UserPushNotifications,
} from '../store/reducers/session-profile.reducer';
import { userProfileFetched, userProfileUpdated, } from '../store/actions/session-profile.actions';
import { isUserRoleProviderSelector } from '../store/selectors/user-role.selectors';
import { userOrganizationSelector } from '../store/selectors/user-fhir.selectors';
import { NativeClientService } from '@shared/services/native-client.service';
import { TranslocoService } from '@jsverse/transloco';

const DEFAULT_PARTNER = {
  code: '0000', //'98972130-f6f1-4888-ba25-964894d57b13',
  display: 'Andere', //'GDG',
};

const DEFAULT_REGION_CODE = 'region_andere';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  public availableRegions$: Observable<DictionaryEntry[]>;
  public availableAgeGroups$: Observable<DictionaryEntry[]>;
  public availableSituations$: Observable<DictionaryEntry[]>;
  public availableCategories$: Observable<DictionaryEntry[]>;
  public availableThemes$: Observable<DictionaryEntry[]>;
  public availablePartners$: Observable<DictionaryEntry[]>;

  public availableLists: { [listName: string]: DictionaryEntry[] } = {};
  isNative: boolean;

  constructor(
    private store: Store,
    private dictionariesCollection: DictionariesCollectionService,
    private translocoService: TranslocoService,
    private userApiService: UserApiService,
    private nativeService: NativeClientService,
  ) {
    this.isNative = this.nativeService.isNative;
  }

  fetchData() {
    this.fetchUserProfile();
  }

  public getDictionaries$(): Observable<any> {
    this.availableAgeGroups$ = this.dictionariesCollection
      .getItemById$('taxonomytermagegroup')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );
    this.availableSituations$ = this.dictionariesCollection
      .getItemById$('taxonomytermaudience')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );
    this.availableCategories$ = this.dictionariesCollection
      .getItemById$('taxonomytermclinicalpicture')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );
    this.availableThemes$ = this.dictionariesCollection
      .getItemById$('taxonomytermsymptom')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );
    this.availablePartners$ = this.dictionariesCollection
      .getItemById$('taxonomytermpartner')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );

    this.availableRegions$ = this.dictionariesCollection
      .getItemById$('taxonomytermregions')
      .pipe(
        map((dict) => dict.list),
        take(1),
      );

    return forkJoin([
      this.availableAgeGroups$,
      this.availableSituations$,
      this.availableCategories$,
      this.availableThemes$,
      this.availablePartners$,
      this.availableRegions$,
    ]).pipe(
      map((availableLists) => {
        [
          this.availableLists.ageGroups,
          this.availableLists.situations,
          this.availableLists.categories,
          this.availableLists.themes,
          this.availableLists.partners,
          this.availableLists.regions,
        ] = availableLists;
      }),
    );
  }

  public fromResource$(
    userProfileResource: UserProfileResource,
  ): Observable<UserProfile> {
    const {
      dashboard_configuration,
      personalInfo,
      onboarding_level,
      onboarding_consent,
      interest_wizard_state,
    } = userProfileResource;

    const regionCode =
      typeof userProfileResource.region === 'string'
        ? userProfileResource.region
        : userProfileResource.region?.code;

    const partnerCode =
      (typeof userProfileResource.partner === 'string'
        ? userProfileResource.partner
        : userProfileResource.partner?.code) ||
      this.getPartnerCodeByRegionCode(regionCode);

    const partnerDisplay = this.getPartnerDisplayByPartnerCode(partnerCode);
    const partnerLogoUrl = this.getPartnerLogoUrlByPartnerCode(partnerCode);
    const regionZip = userProfileResource.region?.zip;

    const userProfile: UserProfile = {
      language: this.translocoService.getActiveLang(),
      dashboard_configuration,
      personalInfo,
      regionCode,
      partnerCode,
      regionZip,
      partnerDisplay,
      partnerLogoUrl,
      interests: this.interestsFromResource(userProfileResource),
      ...this.notificationsFromResource(userProfileResource),
      ...this.pushNotificationsFromResource(userProfileResource),
      onboarding_level,
      onboarding_consent,
      interest_wizard_state,
      _resource: userProfileResource,
    };

    return of(userProfile);
  }

  private toResource(userProfileFull: UserProfile): UserProfileResource {
    const {
      dashboard_configuration,
      language,
      personalInfo,
      regionCode,
      partnerCode,
      regionZip,
      partnerLogoUrl,
      partnerDisplay,
      onboarding_level,
      onboarding_consent,
      interest_wizard_state,
    } = userProfileFull;

    return {
      ...userProfileFull._resource,
      language,
      dashboard_configuration,
      personalInfo,
      interests: this.interestsToResource(userProfileFull),
      region: {
        code: regionCode,
        zip: regionZip,
      },
      partner: {
        code: partnerCode,
        logo_url: partnerLogoUrl,
        display: partnerDisplay,
        system: 'taxonomytermpartner', // Fixed hardcorded value to match value set structure
      },
      'notifications.push': this.pushNotificationsToResource(userProfileFull),
      'notifications.email': this.notificationsToResource(userProfileFull),
      onboarding_level,
      onboarding_consent,
      interest_wizard_state,
    };
  }

  public getPartnerCodeByRegionCode(regionCode: string): string | null {
    const region = this.availableLists.regions.find(
      ({ id }) => id === regionCode,
    );
    return (
      region?.additionalValues?.find(({ use }) => use?.code === 'PARTNER')
        ?.value || DEFAULT_PARTNER.code
    );
  }

  public getRegionCodeByPartnerCode(partnerCode: string): string {
    const region = this.availableLists.regions.find(
      (region) =>
        region?.additionalValues?.find(({ use }) => use?.code === 'PARTNER')
          ?.value === partnerCode,
    );

    return region?.id || DEFAULT_REGION_CODE;
  }

  public getPartnerByCode(partnerCode: string) {
    return this.availableLists.partners.find(({ id }) => partnerCode === id);
  }

  public getPartnerDisplayByPartnerCode(partnerCode: string): string {
    return this.getPartnerByCode(partnerCode)?.name || '';
  }

  public getPartnerLogoUrlByPartnerCode(partnerCode: string): string {
    const partner = this.getPartnerByCode(partnerCode);

    return (
      partner?.additionalValues?.find(({ use }) => use.code === 'LOGO')
        ?.value || ''
    );
  }

  private createEmptyProfile$(): Observable<UserProfileResource> {
    const newUserProfileResource: UserProfileResource = {
      'user-profile.version': '1.0',
      language: this.translocoService.getActiveLang(),
      'notifications.push': {
        type: 'pushNotifications',
        data: [
          { eventTypeName: 'messages', subscription: 'false' },
          { eventTypeName: 'appointments', subscription: 'false' },
          { eventTypeName: 'documents', subscription: 'false' },
          { eventTypeName: 'logbook', subscription: 'false' },
          { eventTypeName: 'transition', subscription: 'false' },
          { eventTypeName: 'cancellation', subscription: 'false' },
          { eventTypeName: 'questionnaires', subscription: 'false' },
          { eventTypeName: 'authorizations', subscription: 'false' },
        ],
      },
      'notifications.email': {
        enabled: 'false',
        topics: {
          messages: 'false',
          appointments: 'false',
          documents: 'false',
          logbook: 'false',
          transition: 'false',
          cancellation: 'false',
          questionnaires: 'false',
          authorizations: 'false',
        },
      },
      region: { code: '', zip: '' },
      onboarding_level: 'NONE',
      interest_wizard_state: 'NOT_STARTED',
    };

    return this.store.pipe(
      select(isUserRoleProviderSelector),
      filter((x) => x != null),
      take(1),
      switchMap((isProvider) =>
        isProvider
          ? this.store.pipe(
              select(userOrganizationSelector),
              filter((x) => x != null),
              take(1),
              switchMap((organization) => {
                newUserProfileResource.onboarding_level = 'FULL';

                const partnerCode =
                  organization?._categories?.['partner']?.[0]?.id;
                if (partnerCode) {
                  const partnerLogoUrl =
                    this.getPartnerLogoUrlByPartnerCode(partnerCode);
                  const partnerDisplay =
                    this.getPartnerDisplayByPartnerCode(partnerCode);

                  newUserProfileResource.partner = {
                    code: partnerCode,
                    logo_url: partnerLogoUrl,
                    display: partnerDisplay,
                    system: 'taxonomytermpartner',
                  };
                }

                return this.userApiService.saveUserProfile(
                  newUserProfileResource,
                );
              }),
            )
          : this.userApiService.saveUserProfile(newUserProfileResource),
      ),
    );
  }

  /**
   * fetch user profile
   */
  fetchUserProfile(): void {
    this.getUserProfile$().subscribe((userProfile: UserProfile) => {
      this.store.dispatch(userProfileFetched({ userProfile }));
    });
  }

  fetchUserForumName(): Observable<any> {
    return this.userApiService.getUserForumName();
  }

  /**
   * get observable of user profile
   * @returns
   */
  public getUserProfile$(): Observable<UserProfile> {
    return this.userApiService.getUserProfile().pipe(
      catchError((error) => {
        if (error && error.status === 404) {
          return this.getDictionaries$().pipe(
            switchMap(() => this.createEmptyProfile$()),
          );
        }
        return throwError(error);
      }),
      switchMap((userProfileResource) => {
        return this.getDictionaries$().pipe(
          switchMap(() => this.fromResource$(userProfileResource)),
        );
      }),
    );
  }

  /**
   * update user profile with new data
   * @param newProfileData
   */
  updateUserProfile$(
    newProfileData: Partial<UserProfile>,
  ): Observable<UserProfile> {
    return this.getUserProfile$().pipe(
      switchMap((userProfile) => {
        const newUserProfile = { ...userProfile, ...newProfileData };

        // if onboarding level is not FULL, should be extra handling of partnerCode
        if (newUserProfile.onboarding_level !== 'FULL') {
          // if partnerCode is explicit changed, should process regionCode
          if (newUserProfile.partnerCode !== userProfile.partnerCode) {
            newUserProfile.regionCode = this.getRegionCodeByPartnerCode(
              newUserProfile.partnerCode,
            );
            newUserProfile.partnerCode =
              newUserProfile.partnerCode ||
              this.getPartnerCodeByRegionCode(newUserProfile.partnerCode);
          } else if (newUserProfile.regionCode !== userProfile.regionCode) {
            newUserProfile.partnerCode = this.getPartnerCodeByRegionCode(
              newUserProfile.regionCode,
            );
          }
        }

        newUserProfile.partnerLogoUrl = this.getPartnerLogoUrlByPartnerCode(
          newUserProfile.partnerCode,
        );
        newUserProfile.partnerDisplay = this.getPartnerDisplayByPartnerCode(
          newUserProfile.partnerCode,
        );

        return this.saveUserProfile$(newUserProfile).pipe(
          tap((res) => {
            if (res) {
              this.store.dispatch(userProfileUpdated({ userProfile: res }));
            }
          }),
        );
      }),
    );
  }

  /**
   * save user profile
   * @param userProfile
   * @returns
   */
  private saveUserProfile$(userProfile: UserProfile): Observable<UserProfile> {
    const userProfileResource = this.toResource(userProfile);

    return this.userApiService.saveUserProfile(userProfileResource).pipe(
      switchMap((savedUserProfileResource) => {
        return this.getDictionaries$().pipe(
          switchMap(() => this.fromResource$(savedUserProfileResource)),
        );
      }),
    );
  }

  public changeLanguage(language: string): void {
    this.updateUserProfile$({ language }).subscribe();
  }

  public changeRegion(regionCode: string): void {
    this.updateUserProfile$({ regionCode }).subscribe();
  }

  public updateNotificationsEnabled(notificationsEnabled: boolean): void {
    this.updateUserProfile$({ notificationsEnabled }).subscribe();
  }

  public updateNotificationsTopicsSetting(value: boolean, topic: string): void {
    this.updateUserProfile$({ [topic]: value }).subscribe();
  }

  public updateNotificationsPushSetting(value: boolean, topic: string): void {
    this.updateUserProfile$({ [topic]: value }).subscribe(() => {
      if (this.isNative) {
        const json = {
          type: 'pushNotifications',
          data: [
            {
              eventTypeName: this.eventTypeNameFromTopic(topic),
              subscription: value,
            },
          ],
        };
        window.alert(JSON.stringify(json));
      }
    });
  }

  public updateOnboardingLevel$(onboarding_level: UserOnboardingLevel) {
    return this.updateUserProfile$({ onboarding_level });
  }

  public updateOnboardingConsent$(): Observable<any> {
    return this.updateUserProfile$({ onboarding_consent: true });
  }

  public updatePartner$(partnerCode: string) {
    return this.updateUserProfile$({ partnerCode });
  }

  private interestsFromResource(
    userProfileResource: UserProfileResource,
  ): UserInterests | undefined {
    const interests = userProfileResource?.interests;

    // We should distinguish "undefined interests" and "no-interest"
    return (
      interests && {
        ageGroup: this.availableLists.ageGroups
          .map(({ id }) => id)
          .find((id) => interests?.some(({ code }) => code === id)),

        situation: this.availableLists.situations
          .map(({ id }) => id)
          .find((id) => interests?.some(({ code }) => code === id)),

        categories: this.availableLists.categories
          .map(({ id }) => id)
          .filter((id) => interests?.some(({ code }) => code === id)),

        themes: this.availableLists.themes
          .map(({ id }) => id)
          .filter((id) => interests?.some(({ code }) => code === id)),
      }
    );
  }

  private interestsToResource(
    userProfile: UserProfile,
  ): UserProfileResource['interests'] {
    const interests = userProfile.interests;

    // We should distinguish "undefined interests" and "no-interest"
    return (
      interests &&
      [
        this.availableLists.ageGroups.find(
          ({ id }) => id === interests.ageGroup,
        ),
        this.availableLists.situations.find(
          ({ id }) => id === interests.situation,
        ),
        ...this.availableLists.categories.filter(
          ({ id }) => interests.categories?.includes(id),
        ),
        ...this.availableLists.themes.filter(
          ({ id }) => interests.themes?.includes(id),
        ),
      ]
        .filter((x) => !!x) // remove empty elements in case of newly created profile
        .map((dictionaryEntry) => {
          const coding: fhir4.Coding = {
            code: dictionaryEntry.id,
            display: dictionaryEntry.name,
          };

          if (dictionaryEntry.system) coding.system = dictionaryEntry.system;

          return coding;
        })
    );
  }

  private notificationsFromResource(
    userProfileResource: UserProfileResource,
  ): UserNotifications {
    const notificationsEmail = userProfileResource['notifications.email'];

    return {
      notificationsEnabled: notificationsEmail?.enabled === 'true',
      notificationsTopicsMessages:
        notificationsEmail?.topics?.messages === 'true',
      notificationsTopicsAppointments:
        notificationsEmail?.topics?.appointments === 'true',
      notificationsTopicsDocuments:
        notificationsEmail?.topics?.documents === 'true',
      notificationsTopicsLogbook:
        notificationsEmail?.topics?.logbook === 'true',
      notificationsTopicsTransition:
        notificationsEmail?.topics?.transition === 'true',
      notificationsTopicsCancellation:
        notificationsEmail?.topics?.cancellation === 'true',
      notificationsTopicsQuestionnaires:
        notificationsEmail?.topics?.questionnaires === 'true',
      notificationsTopicsAuthorizations:
        notificationsEmail?.topics.authorizations === 'true',
    };
  }

  private notificationsToResource(
    userProfile: UserProfile,
  ): UserProfileResource['notifications.email'] {
    return {
      enabled: userProfile.notificationsEnabled ? 'true' : 'false',
      topics: {
        messages: userProfile.notificationsTopicsMessages ? 'true' : 'false',
        appointments: userProfile.notificationsTopicsAppointments
          ? 'true'
          : 'false',
        documents: userProfile.notificationsTopicsDocuments ? 'true' : 'false',
        logbook: userProfile.notificationsTopicsLogbook ? 'true' : 'false',
        transition: userProfile.notificationsTopicsTransition
          ? 'true'
          : 'false',
        cancellation: userProfile.notificationsTopicsCancellation
          ? 'true'
          : 'false',
        questionnaires: userProfile.notificationsTopicsQuestionnaires
          ? 'true'
          : 'false',
        authorizations: userProfile.notificationsTopicsAuthorizations
          ? 'true'
          : 'false',
      },
    };
  }

  private pushNotificationsFromResource(
    userProfileResource: UserProfileResource,
  ): UserPushNotifications {
    const notificationsPush: NotificationsPush =
      userProfileResource['notifications.push'];

    return {
      notificationsPushMessages:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'messages',
        ).subscription === 'true',
      notificationsPushAppointments:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'appointments',
        ).subscription === 'true',
      notificationsPushDocuments:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'documents',
        ).subscription === 'true',
      notificationsPushLogbook:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'logbook',
        )?.subscription === 'true',
      notificationsPushTransition:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'transition',
        )?.subscription === 'true',
      notificationsPushCancellation:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'cancellation',
        )?.subscription === 'true',
      notificationsPushQuestionnaires:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'questionnaires',
        )?.subscription === 'true',
      notificationsPushAuthorizations:
        notificationsPush?.data?.find(
          (entry) => entry.eventTypeName === 'authorizations',
        )?.subscription === 'true',
    };
  }

  private eventTypeNameFromTopic(value: string): string {
    switch (value) {
      case 'notificationsPushMessages':
        return 'messages';
      case 'notificationsPushAppointments':
        return 'appointments';
      case 'notificationsPushDocuments':
        return 'documents';
      case 'notificationsPushLogbook':
        return 'logbook';
      case 'notificationsPushTransition':
        return 'transition';
      case 'notificationsPushCancellation':
        return 'cancellation';
      case 'notificationsPushQuestionnaires':
        return 'questionnaires';
      case 'notificationsPushAuthorizations':
        return 'authorizations';
      default:
        return '';
    }
  }

  private pushNotificationsToResource(
    userProfile: UserProfile,
  ): UserProfileResource['notifications.push'] {
    return {
      type: 'pushNotifications',
      data: [
        {
          eventTypeName: 'messages',
          subscription: userProfile.notificationsPushMessages
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'appointments',
          subscription: userProfile.notificationsPushAppointments
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'documents',
          subscription: userProfile.notificationsPushDocuments
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'logbook',
          subscription: userProfile.notificationsPushLogbook ? 'true' : 'false',
        },
        {
          eventTypeName: 'transition',
          subscription: userProfile.notificationsPushTransition
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'cancellation',
          subscription: userProfile.notificationsPushCancellation
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'questionnaires',
          subscription: userProfile.notificationsPushQuestionnaires
            ? 'true'
            : 'false',
        },
        {
          eventTypeName: 'authorizations',
          subscription: userProfile.notificationsPushAuthorizations
            ? 'true'
            : 'false',
        },
      ],
    };
  }
}
